// src/pages/TikpagExperience/TikpagExperienceEscolhas.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDollarSign,
  Clock3,
  ExternalLink,
  Filter,
  Gift,
  Loader2,
  RefreshCcw,
  Search,
  TicketCheck,
  UserRoundCheck,
  X
} from "lucide-react";

import axios from "../../axiosConfig";
import { getPinImage } from "../../utils/pinAssets";
import "./TikpagExperienceEscolhas.css";

const MESES = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
];

const STATUS_ESCOLHA = [
  ["", "Todos os status"],
  ["CONFIRMADA", "Confirmada"],
  ["EM_PROCESSAMENTO", "Em processamento"],
  ["DISPONIBILIZADA", "Disponibilizada"],
  ["UTILIZADA", "Utilizada"],
  ["EXPIRADA", "Expirada"],
  ["CANCELADA", "Cancelada"]
];

const STATUS_CONQUISTA = [
  ["", "Todos os estágios"],
  ["AGUARDANDO_ESCOLHA", "Aguardando escolha"],
  ["EXPERIENCIA_ESCOLHIDA", "Experiência escolhida"],
  ["EM_ATENDIMENTO", "Em atendimento"],
  ["CONCLUIDA", "Concluída"],
  ["CANCELADA", "Cancelada"]
];

const STATUS_EDITAVEIS = [
  "CONFIRMADA",
  "EM_PROCESSAMENTO",
  "DISPONIBILIZADA",
  "UTILIZADA",
  "EXPIRADA",
  "CANCELADA"
];

const hoje = new Date();

const formatarDinheiro = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const formatarData = (valor) => {
  if (!valor) return "-";
  const [ano, mes, dia] = String(valor).slice(0, 10).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
};

const formatarDataHora = (valor) => {
  if (!valor) return "-";
  const texto = String(valor).replace("T", " ").replace("Z", "");
  const [data, hora] = texto.split(" ");
  return `${formatarData(data)}${hora ? ` ${hora.slice(0, 5)}` : ""}`;
};

const formatarCnpj = (valor) => {
  const numeros = String(valor || "").replace(/\D/g, "");
  if (numeros.length !== 14) return valor || "-";
  return numeros.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

const statusLabel = (status) =>
  String(status || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase());

const FORM_INICIAL = {
  status: "CONFIRMADA",
  codigo_voucher: "",
  link_voucher: "",
  instrucoes_entrega: "",
  validade: "",
  observacoes: ""
};

export default function TikpagExperienceEscolhas() {
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState("");
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [pinId, setPinId] = useState("");
  const [experienciaId, setExperienciaId] = useState("");
  const [statusEscolha, setStatusEscolha] = useState("");
  const [statusConquista, setStatusConquista] = useState("");
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [pagina, setPagina] = useState(1);
  const limite = 25;

  const [dados, setDados] = useState(null);
  const [linhasAbertas, setLinhasAbertas] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCampanhas, setLoadingCampanhas] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [escolhaDetalhe, setEscolhaDetalhe] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const carregarCampanhas = useCallback(async () => {
    setLoadingCampanhas(true);
    setErro("");
    try {
      const resposta = await axios.get("/admin/experience/campanhas");
      const lista = Array.isArray(resposta.data?.campanhas)
        ? resposta.data.campanhas
        : [];

      setCampanhas(lista);
      setCampanhaId((atual) => {
        if (atual) return atual;
        const preferida =
          lista.find((item) => item.status === "EM_ANDAMENTO") ||
          lista.find((item) => item.status === "INSCRICOES") ||
          lista[0];
        return preferida ? String(preferida.id) : "";
      });
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar as campanhas."
      );
    } finally {
      setLoadingCampanhas(false);
    }
  }, []);

  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);

  const carregarEscolhas = useCallback(async () => {
    if (!campanhaId) return;

    setLoading(true);
    setErro("");

    try {
      const resposta = await axios.get("/admin/experience/escolhas", {
        params: {
          campanha_id: Number(campanhaId),
          pagina,
          limite,
          ano: Number(ano) || undefined,
          mes: Number(mes) || undefined,
          pin_id: pinId ? Number(pinId) : undefined,
          experiencia_id: experienciaId ? Number(experienciaId) : undefined,
          status_escolha: statusEscolha || undefined,
          status_conquista: statusConquista || undefined,
          busca: buscaAplicada || undefined
        }
      });

      setDados(resposta.data || null);
    } catch (err) {
      console.error("Erro ao carregar escolhas Experience:", err);
      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar as escolhas dos clientes."
      );
    } finally {
      setLoading(false);
    }
  }, [
    campanhaId,
    pagina,
    ano,
    mes,
    pinId,
    experienciaId,
    statusEscolha,
    statusConquista,
    buscaAplicada
  ]);

  useEffect(() => {
    carregarEscolhas();
  }, [carregarEscolhas]);

  const aplicarBusca = () => {
    setPagina(1);
    setBuscaAplicada(busca.trim());
  };

  const limparFiltros = () => {
    setAno(hoje.getFullYear());
    setMes(hoje.getMonth() + 1);
    setPinId("");
    setExperienciaId("");
    setStatusEscolha("");
    setStatusConquista("");
    setBusca("");
    setBuscaAplicada("");
    setPagina(1);
  };

  const alternarLinha = (id) => {
    setLinhasAbertas((atual) => ({
      ...atual,
      [id]: !atual[id]
    }));
  };

  const abrirEdicao = async (escolhaId) => {
    if (!escolhaId) return;

    setModalAberto(true);
    setLoadingDetalhe(true);
    setEscolhaDetalhe(null);
    setErro("");
    setSucesso("");

    try {
      const resposta = await axios.get(
        `/admin/experience/escolhas/${escolhaId}`
      );

      const detalhe = resposta.data || null;
      setEscolhaDetalhe(detalhe);

      setForm({
        status: detalhe?.escolha?.status || "CONFIRMADA",
        codigo_voucher: detalhe?.escolha?.codigo_voucher || "",
        link_voucher: detalhe?.escolha?.link_voucher || "",
        instrucoes_entrega:
          detalhe?.escolha?.instrucoes_entrega || "",
        validade: detalhe?.escolha?.validade
          ? String(detalhe.escolha.validade).slice(0, 10)
          : "",
        observacoes: detalhe?.escolha?.observacoes || ""
      });
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar os detalhes da escolha."
      );
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const fecharModal = () => {
    if (salvando) return;
    setModalAberto(false);
    setEscolhaDetalhe(null);
    setForm(FORM_INICIAL);
  };

  const atualizarCampo = (campo, valor) => {
    setForm((atual) => ({
      ...atual,
      [campo]: valor
    }));
  };

  const salvarEscolha = async () => {
    const escolhaId = escolhaDetalhe?.escolha?.id;
    if (!escolhaId) return;

    if (
      form.status === "DISPONIBILIZADA" &&
      !form.codigo_voucher.trim() &&
      !form.link_voucher.trim() &&
      !form.instrucoes_entrega.trim()
    ) {
      setErro(
        "Informe o voucher, o link ou as instruções antes de disponibilizar."
      );
      return;
    }

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const resposta = await axios.put(
        `/admin/experience/escolhas/${escolhaId}`,
        {
          status: form.status,
          codigo_voucher: form.codigo_voucher,
          link_voucher: form.link_voucher,
          instrucoes_entrega: form.instrucoes_entrega,
          validade: form.validade || null,
          observacoes: form.observacoes
        }
      );

      setSucesso(
        resposta.data?.message ||
          "Escolha atualizada com sucesso."
      );

      await carregarEscolhas();
      setModalAberto(false);
      setEscolhaDetalhe(null);
      setForm(FORM_INICIAL);
    } catch (err) {
      console.error("Erro ao atualizar escolha Experience:", err);
      setErro(
        err.response?.data?.error ||
          "Não foi possível atualizar a escolha."
      );
    } finally {
      setSalvando(false);
    }
  };

  const escolhas = Array.isArray(dados?.escolhas)
    ? dados.escolhas
    : [];

  const pins = Array.isArray(dados?.filtros?.pins)
    ? dados.filtros.pins
    : [];

  const experiencias = Array.isArray(dados?.filtros?.experiencias)
    ? dados.filtros.experiencias
    : [];

  const paginacao = dados?.paginacao || {
    pagina: 1,
    total_paginas: 1,
    total_registros: 0
  };

  const cards = useMemo(
    () => [
      {
        titulo: "Aguardando escolha",
        valor: dados?.cards?.aguardando_escolha || 0,
        legenda: "Clientes que ainda não escolheram",
        Icone: Clock3,
        classe: "aguardando"
      },
      {
        titulo: "Confirmadas",
        valor: dados?.cards?.confirmadas || 0,
        legenda: "Escolhas recém-confirmadas",
        Icone: CheckCircle2,
        classe: "confirmada"
      },
      {
        titulo: "Em processamento",
        valor: dados?.cards?.em_processamento || 0,
        legenda: "Em atendimento pela equipe",
        Icone: RefreshCcw,
        classe: "processamento"
      },
      {
        titulo: "Disponibilizadas",
        valor: dados?.cards?.disponibilizadas || 0,
        legenda: "Voucher ou instruções enviados",
        Icone: TicketCheck,
        classe: "disponibilizada"
      },
      {
        titulo: "Utilizadas",
        valor: dados?.cards?.utilizadas || 0,
        legenda: "Experiências concluídas",
        Icone: UserRoundCheck,
        classe: "utilizada"
      },
      {
        titulo: "Valor de referência",
        valor: formatarDinheiro(
          dados?.cards?.valor_referencia_total || 0
        ),
        legenda: "Estimativa das escolhas registradas",
        Icone: CircleDollarSign,
        classe: "valor"
      }
    ],
    [dados]
  );

  return (
    <div className="experienceEscolhas-page">
      <header className="experienceEscolhas-header">
        <div>
          <span className="experienceEscolhas-kicker">
            Tikpag Experience
          </span>
          <h1>Escolhas dos clientes</h1>
          <p>
            Acompanhe o funil de experiências, disponibilize vouchers
            e conclua o atendimento dos participantes.
          </p>
        </div>

        <button
          type="button"
          className="experienceEscolhas-refreshButton"
          onClick={carregarEscolhas}
          disabled={loading || loadingCampanhas}
        >
          <RefreshCcw
            size={16}
            className={loading ? "experienceEscolhas-spin" : ""}
          />
          Atualizar
        </button>
      </header>

      {erro && (
        <div className="experienceEscolhas-alert erro">
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="experienceEscolhas-alert sucesso">
          <CheckCircle2 size={17} />
          {sucesso}
        </div>
      )}

      <section className="experienceEscolhas-toolbar">
        <label className="experienceEscolhas-field campanha">
          <span>Campanha</span>
          <select
            value={campanhaId}
            onChange={(event) => {
              setCampanhaId(event.target.value);
              setPagina(1);
            }}
            disabled={loadingCampanhas}
          >
            {!campanhas.length && (
              <option value="">Nenhuma campanha</option>
            )}
            {campanhas.map((campanha) => (
              <option key={campanha.id} value={campanha.id}>
                {campanha.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="experienceEscolhas-field">
          <span>Ano</span>
          <input
            type="number"
            min="2020"
            max="2100"
            value={ano}
            onChange={(event) => {
              setAno(Number(event.target.value));
              setPagina(1);
            }}
          />
        </label>

        <label className="experienceEscolhas-field">
          <span>Mês</span>
          <select
            value={mes}
            onChange={(event) => {
              setMes(Number(event.target.value));
              setPagina(1);
            }}
          >
            {MESES.map((nome, indice) => (
              <option key={nome} value={indice + 1}>
                {nome}
              </option>
            ))}
          </select>
        </label>

        <label className="experienceEscolhas-field">
          <span>PIN</span>
          <select
            value={pinId}
            onChange={(event) => {
              setPinId(event.target.value);
              setPagina(1);
            }}
          >
            <option value="">Todos</option>
            {pins.map((pin) => (
              <option key={pin.id} value={pin.id}>
                {pin.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="experienceEscolhas-field experiencia">
          <span>Experiência</span>
          <select
            value={experienciaId}
            onChange={(event) => {
              setExperienciaId(event.target.value);
              setPagina(1);
            }}
          >
            <option value="">Todas</option>
            {experiencias.map((experiencia) => (
              <option key={experiencia.id} value={experiencia.id}>
                {experiencia.titulo}
              </option>
            ))}
          </select>
        </label>

        <label className="experienceEscolhas-field">
          <span>Status da escolha</span>
          <select
            value={statusEscolha}
            onChange={(event) => {
              setStatusEscolha(event.target.value);
              setPagina(1);
            }}
          >
            {STATUS_ESCOLHA.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="experienceEscolhas-field">
          <span>Estágio da conquista</span>
          <select
            value={statusConquista}
            onChange={(event) => {
              setStatusConquista(event.target.value);
              setPagina(1);
            }}
          >
            {STATUS_CONQUISTA.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <div className="experienceEscolhas-search">
          <Search size={16} />
          <input
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") aplicarBusca();
            }}
            placeholder="Cliente, ID, empresa, experiência ou voucher"
          />
          <button type="button" onClick={aplicarBusca}>
            Buscar
          </button>
        </div>

        <button
          type="button"
          className="experienceEscolhas-clearButton"
          onClick={limparFiltros}
        >
          <Filter size={15} />
          Limpar
        </button>
      </section>

      <section className="experienceEscolhas-cards">
        {cards.map(({ titulo, valor, legenda, Icone, classe }) => (
          <article
            key={titulo}
            className={`experienceEscolhas-card ${classe}`}
          >
            <div><Icone size={20} /></div>
            <span>{titulo}</span>
            <strong>{valor}</strong>
            <small>{legenda}</small>
          </article>
        ))}
      </section>

      <section className="experienceEscolhas-tablePanel">
        <div className="experienceEscolhas-tableHeader">
          <div>
            <h2>Funil de escolhas</h2>
            <p>
              {Number(paginacao.total_registros || 0)} registros encontrados.
            </p>
          </div>
        </div>

        <div className="experienceEscolhas-tableWrapper">
          <table className="experienceEscolhas-table">
            <thead>
              <tr>
                <th />
                <th>Cliente</th>
                <th>Competência</th>
                <th>PIN</th>
                <th>Experiência</th>
                <th>Status</th>
                <th>Escolhida em</th>
                <th>Validade</th>
                <th>Valor</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {escolhas.map((registro) => {
                const aberta = Boolean(
                  linhasAbertas[registro.conquista_id]
                );
                const imagemPin = getPinImage(registro.pin);
                const temEscolha = Boolean(registro.escolha?.id);

                return (
                  <React.Fragment key={registro.conquista_id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="experienceEscolhas-expandButton"
                          onClick={() =>
                            alternarLinha(registro.conquista_id)
                          }
                        >
                          {aberta ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </button>
                      </td>

                      <td>
                        <strong>{registro.cliente.nome}</strong>
                        <small>
                          ID {registro.cliente.id} • {registro.cliente.email}
                        </small>
                        <small>
                          {registro.empresa?.nome || "Empresa não vinculada"}
                        </small>
                      </td>

                      <td>
                        <strong>
                          {String(registro.competencia.mes).padStart(2, "0")}
                          /{registro.competencia.ano}
                        </strong>
                        <small>
                          {formatarDinheiro(registro.volume_apurado)}
                        </small>
                      </td>

                      <td>
                        <div className="experienceEscolhas-pinCell">
                          {imagemPin && (
                            <img src={imagemPin} alt={registro.pin.nome} />
                          )}
                          <div>
                            <strong>{registro.pin.nome}</strong>
                            <small>{registro.pin.status}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        {registro.experiencia ? (
                          <div className="experienceEscolhas-experienceCell">
                            {registro.experiencia.imagem_url && (
                              <img
                                src={registro.experiencia.imagem_url}
                                alt={registro.experiencia.titulo}
                              />
                            )}
                            <div>
                              <strong>{registro.experiencia.titulo}</strong>
                              <small>
                                {registro.experiencia.fornecedor ||
                                  "Fornecedor não informado"}
                              </small>
                            </div>
                          </div>
                        ) : (
                          <span className="experienceEscolhas-noChoice">
                            Aguardando escolha
                          </span>
                        )}
                      </td>

                      <td>
                        <span
                          className={`experienceEscolhas-status ${
                            temEscolha
                              ? String(registro.escolha.status).toLowerCase()
                              : "aguardando_escolha"
                          }`}
                        >
                          {temEscolha
                            ? statusLabel(registro.escolha.status)
                            : "Aguardando escolha"}
                        </span>
                      </td>

                      <td>
                        {formatarDataHora(registro.escolha?.escolhida_em)}
                      </td>

                      <td>
                        {formatarData(registro.escolha?.validade)}
                      </td>

                      <td>
                        <strong>
                          {formatarDinheiro(
                            registro.experiencia?.valor_referencia
                          )}
                        </strong>
                      </td>

                      <td>
                        {temEscolha ? (
                          <button
                            type="button"
                            className="experienceEscolhas-editButton"
                            onClick={() =>
                              abrirEdicao(registro.escolha.id)
                            }
                          >
                            Gerenciar
                          </button>
                        ) : (
                          <span className="experienceEscolhas-waitingAction">
                            Aguardando cliente
                          </span>
                        )}
                      </td>
                    </tr>

                    {aberta && (
                      <tr className="experienceEscolhas-expandedRow">
                        <td colSpan="10">
                          <div className="experienceEscolhas-expandedContent">
                            <div>
                              <span>Empresa</span>
                              <strong>
                                {registro.empresa?.nome || "Não vinculada"}
                              </strong>
                              <small>
                                {formatarCnpj(registro.empresa?.cnpj)}
                              </small>
                            </div>

                            <div>
                              <span>Telefone</span>
                              <strong>
                                {registro.cliente.telefone || "-"}
                              </strong>
                            </div>

                            <div>
                              <span>Código do voucher</span>
                              <strong>
                                {registro.escolha?.codigo_voucher || "-"}
                              </strong>
                            </div>

                            <div>
                              <span>Link do voucher</span>
                              {registro.escolha?.link_voucher ? (
                                <a
                                  href={registro.escolha.link_voucher}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Abrir link
                                  <ExternalLink size={13} />
                                </a>
                              ) : (
                                <strong>-</strong>
                              )}
                            </div>

                            <div>
                              <span>Disponibilizada em</span>
                              <strong>
                                {formatarDataHora(
                                  registro.escolha?.disponibilizada_em
                                )}
                              </strong>
                            </div>

                            <div>
                              <span>Utilizada em</span>
                              <strong>
                                {formatarDataHora(
                                  registro.escolha?.utilizada_em
                                )}
                              </strong>
                            </div>

                            <div className="experienceEscolhas-expandedWide">
                              <span>Instruções</span>
                              <strong>
                                {registro.escolha?.instrucoes_entrega ||
                                  "Nenhuma instrução cadastrada."}
                              </strong>
                            </div>

                            <div className="experienceEscolhas-expandedWide">
                              <span>Observações</span>
                              <strong>
                                {registro.escolha?.observacoes ||
                                  "Nenhuma observação cadastrada."}
                              </strong>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {!loading && escolhas.length === 0 && (
            <div className="experienceEscolhas-empty">
              <Gift size={28} />
              <h3>Nenhuma escolha encontrada</h3>
              <p>
                As conquistas e escolhas aparecerão aqui depois do
                fechamento das competências.
              </p>
            </div>
          )}

          {loading && (
            <div className="experienceEscolhas-loading">
              <Loader2
                size={26}
                className="experienceEscolhas-spin"
              />
              <span>Carregando escolhas...</span>
            </div>
          )}
        </div>

        <footer className="experienceEscolhas-pagination">
          <span>
            Página {paginacao.pagina || 1} de{" "}
            {paginacao.total_paginas || 1}
          </span>

          <div>
            <button
              type="button"
              onClick={() =>
                setPagina((atual) => Math.max(1, atual - 1))
              }
              disabled={
                Number(paginacao.pagina || 1) <= 1 || loading
              }
            >
              <ChevronLeft size={16} />
              Anterior
            </button>

            <button
              type="button"
              onClick={() =>
                setPagina((atual) =>
                  Math.min(
                    Number(paginacao.total_paginas || 1),
                    atual + 1
                  )
                )
              }
              disabled={
                Number(paginacao.pagina || 1) >=
                  Number(paginacao.total_paginas || 1) || loading
              }
            >
              Próxima
              <ChevronRight size={16} />
            </button>
          </div>
        </footer>
      </section>

      {modalAberto && (
        <div
          className="experienceEscolhas-modalOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) fecharModal();
          }}
        >
          <div className="experienceEscolhas-modal">
            <header className="experienceEscolhas-modalHeader">
              <div>
                <span className="experienceEscolhas-kicker">
                  Atendimento da experiência
                </span>
                <h2>Gerenciar escolha</h2>
              </div>

              <button
                type="button"
                onClick={fecharModal}
                disabled={salvando}
              >
                <X size={18} />
              </button>
            </header>

            {loadingDetalhe ? (
              <div className="experienceEscolhas-modalLoading">
                <Loader2
                  size={26}
                  className="experienceEscolhas-spin"
                />
                <span>Carregando detalhes...</span>
              </div>
            ) : escolhaDetalhe ? (
              <>
                <section className="experienceEscolhas-modalSummary">
                  <div className="experienceEscolhas-modalPin">
                    {getPinImage(escolhaDetalhe.pin) && (
                      <img
                        src={getPinImage(escolhaDetalhe.pin)}
                        alt={escolhaDetalhe.pin.nome}
                      />
                    )}
                  </div>

                  <div>
                    <span>{escolhaDetalhe.cliente.nome}</span>
                    <h3>{escolhaDetalhe.experiencia.titulo}</h3>
                    <p>
                      PIN {escolhaDetalhe.pin.nome} •{" "}
                      {String(escolhaDetalhe.conquista.mes).padStart(
                        2,
                        "0"
                      )}
                      /{escolhaDetalhe.conquista.ano}
                    </p>
                  </div>
                </section>

                <div className="experienceEscolhas-formGrid">
                  <label className="experienceEscolhas-modalField">
                    <span>Status</span>
                    <select
                      value={form.status}
                      onChange={(event) =>
                        atualizarCampo("status", event.target.value)
                      }
                    >
                      {STATUS_EDITAVEIS.map((item) => (
                        <option key={item} value={item}>
                          {statusLabel(item)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="experienceEscolhas-modalField">
                    <span>Validade</span>
                    <input
                      type="date"
                      value={form.validade}
                      onChange={(event) =>
                        atualizarCampo("validade", event.target.value)
                      }
                    />
                  </label>

                  <label className="experienceEscolhas-modalField">
                    <span>Código do voucher</span>
                    <input
                      value={form.codigo_voucher}
                      onChange={(event) =>
                        atualizarCampo(
                          "codigo_voucher",
                          event.target.value
                        )
                      }
                      maxLength={200}
                      placeholder="Ex.: TIKPAG-12345"
                    />
                  </label>

                  <label className="experienceEscolhas-modalField">
                    <span>Link do voucher</span>
                    <input
                      value={form.link_voucher}
                      onChange={(event) =>
                        atualizarCampo(
                          "link_voucher",
                          event.target.value
                        )
                      }
                      maxLength={500}
                      placeholder="https://..."
                    />
                  </label>

                  <label className="experienceEscolhas-modalField full">
                    <span>Instruções de entrega</span>
                    <textarea
                      value={form.instrucoes_entrega}
                      onChange={(event) =>
                        atualizarCampo(
                          "instrucoes_entrega",
                          event.target.value
                        )
                      }
                      rows={4}
                      placeholder="Explique como o cliente deverá acessar ou utilizar a experiência."
                    />
                  </label>

                  <label className="experienceEscolhas-modalField full">
                    <span>Observações internas</span>
                    <textarea
                      value={form.observacoes}
                      onChange={(event) =>
                        atualizarCampo(
                          "observacoes",
                          event.target.value
                        )
                      }
                      rows={4}
                      placeholder="Informações internas da equipe administrativa."
                    />
                  </label>
                </div>

                <footer className="experienceEscolhas-modalFooter">
                  <button
                    type="button"
                    className="experienceEscolhas-secondaryButton"
                    onClick={fecharModal}
                    disabled={salvando}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="experienceEscolhas-primaryButton"
                    onClick={salvarEscolha}
                    disabled={salvando}
                  >
                    {salvando ? (
                      <Loader2
                        size={17}
                        className="experienceEscolhas-spin"
                      />
                    ) : (
                      <CheckCircle2 size={17} />
                    )}
                    Salvar alterações
                  </button>
                </footer>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}